/**
 * Le chargement des messages précédents en remontant le fil (`autoLoadEarlier`), en deux règles
 * pures que `<ConversationThread>` applique et que ses tests rejouent :
 *  - quand demander la page précédente : près du haut d'un fil qui défile, comme
 *    `onEndReachedThreshold: 0.3` de l'ancien fil, et une page à la fois ;
 *  - où replacer le fil quand elle arrive : la page s'ajoute AU-DESSUS, donc le fil redescend de ce
 *    qu'elle a ajouté, et le message qu'on lisait reste sous les yeux.
 */

/** Près du haut, à cette part de la hauteur visible, le fil charge la page précédente. */
export const EARLIER_THRESHOLD = 0.3
/** Après l'arrivée d'une page, le temps que le fil se mesure avant de lâcher le message qu'il tenait. */
export const ANCHOR_RELEASE_MS = 500
/** Une page qui n'arrive pas ne retient pas le fil plus longtemps. */
export const ANCHOR_TIMEOUT_MS = 10000

/** La page demandée : la hauteur et la position d'alors, jusqu'à quand elle tient, et si elle est arrivée. */
export type EarlierAnchor = { height: number; offset: number; until: number; arrived: boolean }

export type EarlierScroll = {
    /** La position du haut de la vue dans le fil. */
    offset: number
    /** La hauteur visible. */
    viewport: number
    /** La hauteur du fil entier. */
    content: number
}

/** L'ancre tient encore à cet instant. */
export function isAnchored(anchor: EarlierAnchor | null, now: number): anchor is EarlierAnchor {
    return anchor != null && now <= anchor.until
}

/** Faut-il demander la page précédente ? Rend l'ancre à poser, ou `null`. */
export function earlierAnchorFor(
    scroll: EarlierScroll,
    current: EarlierAnchor | null,
    now: number
): EarlierAnchor | null {
    const nearTop = scroll.offset < scroll.viewport * EARLIER_THRESHOLD
    // Un fil plus court que la vue ne défile pas : le bouton « Voir les messages précédents » reste.
    const scrolls = scroll.content > scroll.viewport
    if (!nearTop || !scrolls) return null
    // Une page à la fois : la suivante attend que la précédente soit arrivée.
    if (isAnchored(current, now) && !current.arrived) return null
    return { height: scroll.content, offset: scroll.offset, until: now + ANCHOR_TIMEOUT_MS, arrived: false }
}

/** La page est arrivée : l'ancre tient encore le temps que le fil se mesure. */
export function arrivedAnchor(anchor: EarlierAnchor, now: number): EarlierAnchor {
    return { ...anchor, arrived: true, until: now + ANCHOR_RELEASE_MS }
}

/** Le fil a grandi de ce qui s'est ajouté au-dessus : il redescend d'autant. */
export function anchoredOffset(anchor: EarlierAnchor, contentHeight: number): number {
    return anchor.offset + contentHeight - anchor.height
}
